import axiosInstance from "./api";
import { getPromptsFromTopicVersion } from "./promptService";
import { getTestConfigsByTopicKey } from "./testConfigService";
import { v4 as uuidv4 } from "uuid";

const TOPICS_API = "/api/topic";

export const createTopic = async ({
  name,
  key,
  description,
  tags = [],
  test_configs = [],
}) => {
  const result = await axiosInstance.post(`${TOPICS_API}/`, {
    name,
    key,
    description,
    tags,
    test_configs,
  });
  return result.data.data;
};

export const createTopicVersion = async ({
  topic_key,
  prompt_id,
  version_comment,
  visibility,
  supported_modalities,
  fragments,
  prompts,
}) => {
  if (!prompts || prompts.length === 0) {
    prompts = [
      {
        content: "{# primary content prompt #}",
        file_name: "content",
        file_type: "jinja",
        prompt_type: "primary",
        order_index: 0,
        skill_name: "disabled",
      },
    ];
  }
  try {
    const result = await axiosInstance.post(
      `${TOPICS_API}/${topic_key}/version`,
      {
        prompt_id,
        version_comment,
        visibility,
        supported_modalities,
        fragments,
        prompts,
      },
    );
    if (result.status !== 200 && result.status !== 201) {
      console.error("There was an error creating topic version.");
      throw new Error(
        result.data?.error || "There was an error creating topic version.",
      );
    }
    return result.data.data;
  } catch (error) {
    console.error(
      "Error creating topic version:",
      error?.response?.data?.error || error.message,
    );
    throw new Error(error?.response?.data?.error || error.message);
  }
};

export const duplicateTopic = async (key) => {
  try {
    const originalTopic = await getTopicByKey(key);
    const originalTopicLatestVersion = await getLatestTopicVersion(key);
    const originalTopicLatestVersionFragments =
      await getPromptFragmentsByTopicVersion(
        key,
        originalTopicLatestVersion.version,
      );
    const originalTestConfigs = await getTestConfigsByTopicKey(key);
    const originalPrompts = await getPromptsFromTopicVersion(
      originalTopicLatestVersion.id,
    );

    const rand = Math.floor(1000 + Math.random() * 9000);
    const newName = `${originalTopic.name} (Copy ${rand})`;

    const result = await createTopic({
      name: newName,
      key: uuidv4(),
      description: originalTopic.description,
      tags: originalTopic?.tags || [],
      test_configs: originalTestConfigs || [],
    });

    await createTopicVersion({
      topic_key: result.key,
      prompt_id: null,
      version_comment: `Duplicated from ${originalTopic.name}`,
      visibility: originalTopicLatestVersion.visibility,
      supported_modalities: originalTopicLatestVersion.supported_modalities,
      fragments: originalTopicLatestVersionFragments,
      prompts: originalPrompts || [],
    });

    return result;
  } catch (error) {
    console.error("Error duplicating Topic:", error);
    throw error;
  }
};

export const updateTopic = async ({
  key,
  name,
  description,
  tags = null,
  test_configs = null,
  is_deleted = false,
}) => {
  const deletedQuery = is_deleted ? `?is_deleted=${is_deleted}` : "";
  let result;
  if (is_deleted === true) {
    result = await axiosInstance.put(`${TOPICS_API}/${key}${deletedQuery}`, {
      name,
      description,
      tags,
      test_configs,
    });
  } else {
    result = await axiosInstance.put(`${TOPICS_API}/${key}${deletedQuery}`, {
      name,
      description,
      tags,
      test_configs,
      deleted_at: null,
      deleted_by: null,
    });
  }
  return result.data.data;
};

export const updateTopicVersion = async ({
  topic_key,
  version,
  version_comment,
  visibility,
  supported_modalities,
}) => {
  const result = await axiosInstance.put(
    `${TOPICS_API}/${topic_key}/version/${version}`,
    {
      version_comment,
      visibility,
      supported_modalities,
    },
  );
  return result.data.data;
};

export const getAllTopics = async () => {
  const topics = await axiosInstance.get(`${TOPICS_API}/`);
  if (!topics.data?.data?.length > 0) {
    return null;
  }
  return topics.data.data;
};

export const getAllTopicsDetails = async () => {
  const topics = await axiosInstance.get(`${TOPICS_API}/detailed`);
  if (!topics.data?.data?.length > 0) {
    return null;
  }
  return topics.data.data;
};

export const getTopicByKey = async (key) => {
  const topics = await axiosInstance.get(`${TOPICS_API}/${key}`);
  if (!topics.data?.data) {
    return null;
  }
  return topics.data.data;
};

export const getTopicByVersion = async (key, version) => {
  const topics = await axiosInstance.get(
    `${TOPICS_API}/${key}/version/${version}`,
  );
  return topics.data.data;
};

export const getLatestTopicVersion = async (key) => {
  const topics = await axiosInstance.get(`${TOPICS_API}/${key}/latest_version`);
  return topics.data.data;
};

export const getPromptFragmentsByTopicVersion = async (key, version) => {
  const topics = await axiosInstance.get(
    `${TOPICS_API}/${key}/version/${version}/fragments`,
  );
  return topics.data.data;
};

export const getTopicVersionWithPrompts = async (key, version) => {
  const topics = await axiosInstance.get(
    `${TOPICS_API}/${key}/version/${version}/fulldata`,
  );
  return topics.data.data;
};

export const getLatestTopicByVisibility = async (visibility) => {
  try {
    const result = await axiosInstance.get(
      `${TOPICS_API}/visibility/${visibility}`,
    );
    return result.data?.data || [];
  } catch (error) {
    console.error("Error fetching topics by visibility:", error);
    throw error;
  }
};

export const getTopicCardDataForKey = async (topic_key) => {
  const result = await axiosInstance.get(
    `${TOPICS_API}/${topic_key}/topic_card_data`,
  );
  return result.data?.data || {};
};

export const getLatestVisibilitiesForTopics = async () => {
  const result = await axiosInstance.get(`${TOPICS_API}/visibilities`);
  return result.data?.data || {};
};

export const getDeletedTopics = async () => {
  const topics = await axiosInstance.get(`${TOPICS_API}/deleted`);
  if (!topics.data?.data?.length > 0) {
    return [];
  }
  return topics.data.data;
};

export const getReleaseNotesByTopicKey = async (key) => {
  const result = await axiosInstance.get(`${TOPICS_API}/${key}/release_notes`);
  return result.data?.data || null;
};

export const getVersionsWithPrompts = async (topic_key) => {
  try {
    const result = await axiosInstance.get(
      `${TOPICS_API}/${topic_key}/versions_with_prompts`,
    );
    return result.data?.data || [];
  } catch (error) {
    console.error(
      `Error fetching versions with prompts for topic ${topic_key}:`,
      error,
    );
    throw error;
  }
};

export const exportPromptsByTopicVersions = async (versionsList) => {
  if (!versionsList || versionsList.length === 0) {
    console.error("No topic versions provided for export.");
    throw new Error("No topic versions provided for export.");
  }
  try {
    const idsQuery = versionsList
      .map((v) => `topic_version_ids=${v}`)
      .join("&");
    const result = await axiosInstance.get(`${TOPICS_API}/export?${idsQuery}`);
    return result.data?.data || null;
  } catch (error) {
    console.error("Error exporting prompts by topic versions:", error);
    throw error;
  }
};

export const importTopics = async ({
  topicVersionsImports,
  forced = false,
}) => {
  try {
    const forcedQuery = forced ? "?forced=true" : "";
    const result = await axiosInstance.post(
      `${TOPICS_API}/import${forcedQuery}`,
      topicVersionsImports,
    );
    if (result.data?.error && result.data.error.length > 0) {
      console.error("Errors during topic import:", result.data.error);
      throw new Error(
        `Errors during topic import: ${result.data.error.join(", ")}`,
      );
    }
    return result.data?.data || [];
  } catch (error) {
    console.error("Error importing topics:", error);
    throw error;
  }
};

export const getAllConversationProcessingJobs = async () => {
  const topics = await axiosInstance.get(`${TOPICS_API}/jobs`);
  return topics.data.data;
};

// Export the function with the rest of the service exports
export const topicService = {
  createTopic,
  createTopicVersion,
  duplicateTopic,
  updateTopic,
  updateTopicVersion,
  getAllConversationProcessingJobs,
  getAllTopics,
  getAllTopicsDetails,
  getTopicByKey,
  getTopicByVersion,
  getReleaseNotesByTopicKey,
  getLatestTopicVersion,
  getLatestTopicByVisibility,
  getDeletedTopics,
  getPromptFragmentsByTopicVersion,
  getTopicVersionWithPrompts,
};

export default topicService;
